/**
 * CW-SF-D1 TKT-5 — Team Pipeline by Stage widget.
 *
 * Same SVG bar chart as M5 Pipeline-by-Stage; the difference is the
 * backend query (no `OwnerId = me` filter). Per-viewer field-mask
 * intersection still applies — totals legitimately differ by viewer,
 * footer makes the variance explicit via "Visible to you: $X".
 */
import { useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface StageBucket {
  stage: string;
  total: number;
  count: number;
}
interface ApiResponse extends SfWidgetEnvelope {
  stages: StageBucket[];
  amount_denied: boolean;
  total_visible: number;
  opportunity_count: number;
}

const REFRESH_S = 5 * 60;

export function SfTeamPipelineByStageWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/team-pipeline-by-stage', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  const buckets = data?.stages ?? [];
  const maxTotal = buckets.length ? Math.max(...buckets.map((b) => b.total)) : 0;

  return (
    <div className="sf-widget sf-widget--pipeline">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={buckets.length > 0}
        emptyMessage={
          data?.amount_denied
            ? "Your Salesforce profile doesn't have access to Opportunity Amount."
            : 'No open team pipeline visible to you.'
        }
      >
        <ul className="sf-widget__bars" aria-label="Team pipeline by stage">
          {buckets.map((b) => {
            const pct = maxTotal ? (b.total / maxTotal) * 100 : 0;
            return (
              <li key={b.stage} className="sf-widget__bar">
                <div className="sf-widget__bar-label">
                  <span>{b.stage}</span>
                  <span className="sf-widget__bar-meta">
                    {formatCurrency(b.total)} · {b.count}
                  </span>
                </div>
                <svg
                  className="sf-widget__bar-track"
                  viewBox="0 0 100 6"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={`${b.stage}: ${formatCurrency(b.total)}`}
                >
                  <rect x="0" y="0" width="100" height="6" rx="2" className="sf-widget__bar-bg" />
                  <rect x="0" y="0" width={pct} height="6" rx="2" className="sf-widget__bar-fill" />
                </svg>
              </li>
            );
          })}
        </ul>
        {data && data.total_visible > 0 && (
          <div className="sf-widget__footer-meta">
            Visible to you: {formatCurrency(data.total_visible)} · {data.opportunity_count} opps
          </div>
        )}
      </SfWidgetShell>
    </div>
  );
}
